import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase.js";
import { authUrl, authUrlIsEmpty, scrubAuthUrl } from "../lib/authUrl.js";
import { requestInviteResend } from "../lib/storage.js";
import { C } from "../theme/tokens.js";

// ============================================================================
//  Where a man lands from an invite email or a password reset.
//
//  Two link shapes arrive here, and both are handled:
//
//   1. ?token_hash=...&type=invite  — the current email templates. The link
//      points at this page, and the one-time token is spent by a POST from the
//      browser. A mail scanner that follows the link fetches a plain HTML page
//      and takes nothing with it.
//
//   2. #access_token=... or #error=...  — the older link, which sent the man
//      through Supabase's /verify endpoint first. Still arriving from any
//      email sent before the templates changed, so it keeps working.
//
//  The thing this page gets wrong at its peril is failure. An expired link is
//  the single most common way a man does not get into Mile Marker, and the
//  previous version answered it with one sentence, no button and no way out —
//  it told him to go ask his mentor. The recovery box is now on this page,
//  because this page is where he actually is.
// ============================================================================

// Supabase's own wording for these is accurate but reads like a stack trace.
function explain(code, description) {
  switch (code) {
    case "otp_expired":
      return "That link has expired. Links are good for 24 hours and work only once, so an old email or a second click will always land here.";
    case "access_denied":
      return "That link is no longer valid. It may have expired, or already been used.";
    case "email_change_token_invalid":
    case "validation_failed":
      return "That link was not complete. It can be broken by an email app that wraps long links across lines.";
    default:
      return description || "That link did not work.";
  }
}

export default function AuthCallback() {
  const nav = useNavigate();
  // working → checking the link · password → link was good, set one · failed
  const [phase, setPhase] = useState("working");
  const [problem, setProblem] = useState("");
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const done = (next, why = "") => {
      if (cancelled) return;
      setProblem(why);
      setPhase(next);
      scrubAuthUrl();
    };

    (async () => {
      // 1. The link itself already told us it failed.
      if (authUrl.error) {
        done("failed", explain(authUrl.errorCode, authUrl.errorDescription));
        return;
      }

      // 2. Current templates: spend the token from here.
      if (authUrl.tokenHash) {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: authUrl.tokenHash,
          type: authUrl.type || "invite",
        });
        if (error) {
          done("failed", explain(error.code, error.message));
          return;
        }
        // A magic link is a sign-in and nothing more; everything else that
        // reaches this page is a man who needs a password.
        if (authUrl.type === "magiclink" || authUrl.type === "email") {
          scrubAuthUrl();
          nav("/", { replace: true });
          return;
        }
        done("password");
        return;
      }

      // 3. Older links: supabase-js has already turned the fragment into a
      //    session by the time this runs. If it did not, the link was bad.
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        done("password");
        return;
      }
      done(
        "failed",
        authUrlIsEmpty
          ? "This page is the last step of an invite or a password reset, and it was opened without one. Start from the sign-in page."
          : explain(null, null),
      );
    })();

    return () => { cancelled = true; };
  }, [nav]);

  async function setPassword(e) {
    e.preventDefault();
    if (pw.length < 8) { setMsg("Use at least 8 characters."); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setSaving(false);
    if (error) { setMsg(error.message); return; }
    nav("/", { replace: true });
  }

  return (
    <Shell>
      {phase === "working" && (
        <>
          <H>Checking your link</H>
          <P>One moment.</P>
        </>
      )}

      {phase === "password" && (
        <>
          <H>Set your password</H>
          <P>Pick something you will remember. You will use it to sign in from now on.</P>
          <form onSubmit={setPassword}>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="At least 8 characters" autoComplete="new-password" style={inp} />
            <button type="submit" disabled={saving} style={{ ...btn, opacity: saving ? 0.7 : 1 }}>
              {saving ? "…" : "Save and continue"}
            </button>
          </form>
          {msg && <div style={{ color: C.redDk, fontSize: 13, marginTop: 10 }}>{msg}</div>}
        </>
      )}

      {phase === "failed" && <Recovery problem={problem} />}
    </Shell>
  );
}

/**
 * The whole point of the rewrite. A man whose link failed can fix it here,
 * without knowing another URL exists and without needing anyone else.
 */
function Recovery({ problem }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);

  async function send(e) {
    e.preventDefault();
    setBusy(true);
    try {
      // Same deliberately uninformative response as the login page: this box
      // is reachable without a session, so it must not reveal who is on the
      // roster. See supabase/functions/resend-invite.
      const data = await requestInviteResend(email);
      setSent(data?.message || "If that email is on the roster, a fresh link is on its way.");
    } catch (err) {
      setSent(err.message || "Something went wrong. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <H>Let's get you a new link</H>
      <P>{problem}</P>

      {sent ? (
        <div style={{
          marginTop: 18, padding: "13px 15px", borderRadius: 10,
          background: "#F6F8F8", border: `1px solid ${C.line}`,
          fontSize: 13.5, lineHeight: 1.55, color: C.sub,
        }}>
          {sent}
        </div>
      ) : (
        <form onSubmit={send} style={{ marginTop: 18 }}>
          <label style={lbl}>The email your invite was sent to</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            autoComplete="email" style={inp} />
          <button type="submit" disabled={busy} style={{ ...btn, opacity: busy ? 0.7 : 1 }}>
            {busy ? "…" : "Send me a new link"}
          </button>
          <p style={{ fontSize: 12.5, color: C.faint, lineHeight: 1.5, marginTop: 12 }}>
            Open the new one the same day. If nothing arrives within a few
            minutes, check your spam or junk folder.
          </p>
        </form>
      )}

      <div style={{ marginTop: 20, paddingTop: 14, borderTop: `1px solid ${C.line}` }}>
        {/* A full page load, not a router link: this clears any half-built
            auth state the failed link left behind. */}
        <a href="/login" style={{ fontSize: 13, color: C.sub }}>
          Already set your password? Sign in
        </a>
      </div>
    </>
  );
}

// ---- small local presentation pieces ---------------------------------------
// This page renders before the app shell injects Barlow or the global reset,
// so it carries its own box-sizing, the same way the login page does.

const Shell = ({ children }) => (
  <div style={{
    minHeight: "100vh", display: "grid", placeItems: "center",
    background: C.shell, padding: 20, fontFamily: "system-ui, sans-serif", color: C.ink,
  }}>
    <div style={{
      background: C.panel, padding: 32, borderRadius: 18, maxWidth: 420, width: "100%",
      boxSizing: "border-box", border: `1px solid ${C.line}`,
      boxShadow: "0 20px 60px rgba(19,34,41,0.10)",
    }}>
      <div style={{ fontSize: 11, letterSpacing: ".22em", color: C.red, fontWeight: 700 }}>
        MILE MARKER
      </div>
      {children}
    </div>
  </div>
);

const H = ({ children }) => (
  <h1 style={{ fontSize: 24, margin: "6px 0 6px", letterSpacing: ".01em" }}>{children}</h1>
);

const P = ({ children }) => (
  <p style={{ fontSize: 13.5, color: C.sub, marginTop: 0, lineHeight: 1.55 }}>{children}</p>
);

const lbl = { fontSize: 12.5, fontWeight: 600, color: C.sub, marginBottom: 6, display: "block" };

const inp = {
  width: "100%", padding: "10px 13px", borderRadius: 9,
  border: `1.5px solid ${C.line}`, background: "#FBFBFB",
  fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box",
};

const btn = {
  marginTop: 14, width: "100%", padding: "11px 16px", borderRadius: 9,
  background: C.red, color: "#fff", border: "none", fontWeight: 700, fontSize: 14,
  cursor: "pointer",
};
